/* Moments Etched v15.0.5 — Theme Editor drag/resize placement calibration */
(() => {
  if (!window.Shopify || !Shopify.designMode) return;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const round = (value) => Math.round(value * 10) / 10;

  const initWorkspace = (workspace) => {
    if (!workspace || workspace.dataset.meCalReady === 'true') return;
    const stage = workspace.querySelector('[data-me-cal-stage]');
    const boundary = workspace.querySelector('[data-me-cal-boundary]');
    if (!stage || !boundary) return;

    workspace.dataset.meCalReady = 'true';

    const read = (name, fallback = 0) => {
      const raw = parseFloat(boundary.dataset[name]);
      return Number.isFinite(raw) ? raw : fallback;
    };

    let state = {
      x: read('x', 50),
      y: read('y', 30),
      w: read('w', 10),
      h: read('h', 10),
    };

    const valueNodes = {
      x: workspace.querySelector('[data-me-cal-value="x"]'),
      y: workspace.querySelector('[data-me-cal-value="y"]'),
      w: workspace.querySelector('[data-me-cal-value="w"]'),
      h: workspace.querySelector('[data-me-cal-value="h"]'),
    };
    const copyAll = workspace.querySelector('[data-me-cal-copy-all]');
    const status = workspace.querySelector('[data-me-cal-status]');

    const render = () => {
      boundary.style.setProperty('--me-cal-x', `${state.x}%`);
      boundary.style.setProperty('--me-cal-y', `${state.y}%`);
      boundary.style.setProperty('--me-cal-w', `${state.w}%`);
      boundary.style.setProperty('--me-cal-h', `${state.h}%`);
      Object.entries(valueNodes).forEach(([key, node]) => {
        if (node) node.textContent = `${state[key]}%`;
      });
      if (copyAll) copyAll.dataset.copyValue = `X ${state.x} | Y ${state.y} | W ${state.w} | H ${state.h}`;
    };

    const setStatus = (message) => {
      if (!status) return;
      status.textContent = message;
      window.clearTimeout(status._meTimer);
      status._meTimer = window.setTimeout(() => { status.textContent = ''; }, 2600);
    };

    const stagePercent = (clientX, clientY) => {
      const rect = stage.getBoundingClientRect();
      return {
        x: ((clientX - rect.left) / rect.width) * 100,
        y: ((clientY - rect.top) / rect.height) * 100,
      };
    };

    let interaction = null;
    const onPointerMove = (event) => {
      if (!interaction) return;
      event.preventDefault();
      const point = stagePercent(event.clientX, event.clientY);
      const dx = point.x - interaction.startPoint.x;
      const dy = point.y - interaction.startPoint.y;
      const start = interaction.start;

      if (interaction.mode === 'move') {
        state.x = round(clamp(start.x + dx, start.w / 2, 100 - start.w / 2));
        state.y = round(clamp(start.y + dy, 0, 100 - start.h));
      } else {
        const minW = 2;
        const minH = 2;
        const left0 = start.x - start.w / 2;
        const right0 = start.x + start.w / 2;
        const top0 = start.y;
        const bottom0 = start.y + start.h;
        let left = left0;
        let right = right0;
        let top = top0;
        let bottom = bottom0;

        if (interaction.handle.includes('e')) right = clamp(right0 + dx, left + minW, 100);
        if (interaction.handle.includes('w')) left = clamp(left0 + dx, 0, right - minW);
        if (interaction.handle.includes('s')) bottom = clamp(bottom0 + dy, top + minH, 100);
        if (interaction.handle.includes('n')) top = clamp(top0 + dy, 0, bottom - minH);

        state.w = round(right - left);
        state.h = round(bottom - top);
        state.x = round(left + state.w / 2);
        state.y = round(top);
      }
      render();
    };

    const endInteraction = () => {
      if (!interaction) return;
      interaction = null;
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', endInteraction);
      document.body.classList.remove('me-cal-is-dragging');
      setStatus('Position updated. Enter the displayed values in the Theme Editor sidebar to save.');
    };

    boundary.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      const handle = event.target.closest('[data-me-cal-handle]');
      interaction = {
        mode: handle ? 'resize' : 'move',
        handle: handle ? handle.dataset.meCalHandle : '',
        startPoint: stagePercent(event.clientX, event.clientY),
        start: { ...state },
      };
      event.preventDefault();
      document.body.classList.add('me-cal-is-dragging');
      document.addEventListener('pointermove', onPointerMove, { passive: false });
      document.addEventListener('pointerup', endInteraction, { once: true });
    });

    boundary.addEventListener('keydown', (event) => {
      const step = event.altKey ? 0.1 : (event.shiftKey ? 1 : 0.5);
      let handled = true;
      if (event.ctrlKey || event.metaKey) {
        if (event.key === 'ArrowLeft') state.w = round(clamp(state.w - step, 2, 100));
        else if (event.key === 'ArrowRight') state.w = round(clamp(state.w + step, 2, 100));
        else if (event.key === 'ArrowUp') state.h = round(clamp(state.h - step, 2, 100));
        else if (event.key === 'ArrowDown') state.h = round(clamp(state.h + step, 2, 100));
        else handled = false;
      } else {
        if (event.key === 'ArrowLeft') state.x = round(clamp(state.x - step, state.w / 2, 100 - state.w / 2));
        else if (event.key === 'ArrowRight') state.x = round(clamp(state.x + step, state.w / 2, 100 - state.w / 2));
        else if (event.key === 'ArrowUp') state.y = round(clamp(state.y - step, 0, 100 - state.h));
        else if (event.key === 'ArrowDown') state.y = round(clamp(state.y + step, 0, 100 - state.h));
        else handled = false;
      }
      if (!handled) return;
      event.preventDefault();
      render();
      setStatus('Adjusted. Copy these values into the Theme Editor sidebar to save.');
    });

    workspace.querySelectorAll('[data-me-cal-copy]').forEach((button) => {
      button.addEventListener('click', async () => {
        const key = button.dataset.meCalCopy;
        const text = String(state[key]);
        try {
          await navigator.clipboard.writeText(text);
          setStatus(`${key.toUpperCase()} value ${text} copied.`);
        } catch (_) {
          setStatus(`${key.toUpperCase()}: ${text}`);
        }
      });
    });

    if (copyAll) {
      copyAll.addEventListener('click', async () => {
        const text = `X ${state.x} | Y ${state.y} | W ${state.w} | H ${state.h}`;
        try {
          await navigator.clipboard.writeText(text);
          setStatus('All four calibration values copied.');
        } catch (_) {
          setStatus(text);
        }
      });
    }

    render();
  };

  const initAll = () => document.querySelectorAll('[data-me-cal-workspace]').forEach(initWorkspace);
  initAll();
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', (event) => {
    initAll();
    const workspace = document.querySelector('[data-me-cal-workspace]');
    if (!workspace) return;
    const block = event.target;
    if (block && block.matches && block.matches('[data-me-cal-workspace]')) {
      workspace.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
})();
